import React, { useMemo, useRef, useState } from 'react';
import { LayoutRectangle, ScrollView, ScrollViewProps } from 'react-native';
import { Platform } from 'src/libs/platform';
import { usePlatformPadding } from 'src/hooks/usePlatformPadding';
import { isFloat, isNumber } from 'src/utils/other';

export type CardProps = ScrollViewProps & {
  /**
   * Height depends on the size:
   * s - 92%
   * m - 62%
   * l - 30%
   */
  size?: 's' | 'm' | 'l' | number;
  /**
   * Size of the gap between cards
   */
  gap?: number;
  /**
   * Shows horizontal padding. Useful when used outside of any containers.
   */
  padding?: boolean;
};

export const CardScroll: React.FC<CardProps> = ({
  children,
  size = 's',
  gap = 8,
  padding = false,
  style,
  ...props
}) => {
  const [dimensions, setDimensions] = useState<LayoutRectangle>();
  const { left, right } = usePlatformPadding(true);
  const scrollRef = useRef(null);

  const cardsCount = React.Children.count(children);

  const width = useMemo(() => {
    if (!dimensions?.width) {
      return 0;
    }

    const totalGap = padding ? left + right : 0;

    switch (size) {
      case 's':
        return (dimensions.width - totalGap) * 0.4;
      case 'm':
        return (dimensions.width - totalGap) * 0.62;
      case 'l':
        return dimensions.width - totalGap;
      default:
        return !isFloat(size) ? size : (dimensions.width - totalGap) * size;
    }
  }, [dimensions?.width, left, padding, right, size]);

  const isFullSize = size === 'l' || (isFloat(size) && size === 1);
  const snapInterval = isFullSize ? width + right : width + gap;

  return (
    <ScrollView
      // @ts-ignore
      dataSet={{
        scrollSnapType: 'x',
        ...(Platform.OS === 'ios' && { scrollPaddingIos: true }),
        ...(Platform.OS !== 'ios' && { scrollPaddingAndroid: true }),
      }}
      contentContainerStyle={[
        style,
        padding && {
          paddingLeft: left,
          paddingRight: right,
        },
      ]}
      decelerationRate={0}
      snapToInterval={snapInterval}
      // snapToStart={true}
      showsHorizontalScrollIndicator={false}
      {...props}
      horizontal
      onLayout={event => setDimensions(event.nativeEvent.layout)}
      ref={scrollRef}
      accessibilityRole="adjustable"
    >
      {React.Children.map<any, any>(children, (child, i) =>
        React.cloneElement(child, {
          key: i,
          dataSet: { scrollSnapAlign: isFullSize ? 'center' : 'start' },
          style: [
            child.props.style,
            i !== cardsCount - 1 && { marginRight: isFullSize ? right : gap },
            { width },
          ],
        })
      )}
    </ScrollView>
  );
};
